import React from "react";
import { useState } from "react";
import Layout from "../components/layout/Layout";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Container,
  Hidden,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Apple,
  Android,
  Delete,
  DriveFolderUpload,
  Home,
  Search,
  Smartphone,
} from "@mui/icons-material";
import { formatDistance } from "date-fns";
import {
  Application4Table,
  Application,
} from "../components/apps-components/apps-types";
import AppsCard from "../components/apps-components/ApplicationsCard";
import DeleteElementFromTable from "../components/reusable/DeleteElementFromTableDialog";
import Footer from "../components/layout/Footer";
import NoDataToBeShown from "../components/reusable/NoDataToBeShown";
import ErrorComponent from "../components/reusable/ErrorComponent";
import UploadingAppForm from "../components/apps-components/UploadingApplication";
import { enUS, it } from "date-fns/locale";
import checkPlatform from "../utils/checkPlatform";
import { grey } from "@mui/material/colors";
import { useApps, useGroups } from "./api/api";
import reportWebVitals from "../reportWebVitals";

export default function ApplicationsPage() {
  const { t } = useTranslation();

  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  const { apps, isLoadingApps, isErrorApps } = useApps();
  const [searchApp, setSearchApp] = useState("");

  const { groups } = useGroups();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [orderDirection, setOrderDirection] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("name");

  const [cardOrder, setCardOrder] = useState(t("Latest"));
  const [groupId, setGroupId] = useState<number | undefined>();

  const [openDelete, setOpenDelete] = useState(false);
  const [deletedApp, setDeletedApp] = useState<
    Application4Table | Application | undefined
  >(undefined);

  const skeletonArray = Array(5).fill("");
  const title = t("Delete");

  // To enable the Sorting Table function, it is necessary to transform types into numbers/strings
  const data4Table: Application4Table[] = apps?.map((app) => {
    return {
      id: app.id,
      icon: app.icon,
      name: app.name,
      description: app.description,
      updated_at: app.updated_at,
      groupId: app.groupId,
      platform: checkPlatform(app.versions),
    };
  });

  //const [length, setLength] = useState<number>(apps.length);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  type Order = "asc" | "desc";

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const handleSetDeleteInfo = (element: Application4Table) => {
    setOpenDelete(true);
    setDeletedApp(element);
  };

  const handleRequestSort = (property: keyof Application4Table) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSearch = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    console.log("page", page);
    console.log("event", event.target.value);
    setPage(0);
    setSearchApp(event.target.value);
  };

  const handleMenuItem = (group: number | undefined) => {
    setGroupId(group);
    handleClose();
  };

  function sortCards(field: string) {
    switch (field) {
      case "ascending-name":
        apps?.sort((a, b) => (a.name > b.name ? 1 : -1));
        setCardOrder(t("Name"));
        break;

      case "descending-name":
        apps?.sort((a, b) => (a.name < b.name ? 1 : -1));
        break;

      case "descending-time":
        apps?.sort((a, b) =>
          new Date(a.updated_at).getTime() < new Date(b.updated_at).getTime()
            ? 1
            : -1
        );
        setCardOrder(t("Latest"));
        break;

      case "ascending-time":
        apps?.sort((a, b) =>
          new Date(a.updated_at).getTime() > new Date(b.updated_at).getTime()
            ? 1
            : -1
        );
        setCardOrder(t("Oldest"));
        break;

      default:
        break;
    }

    handleClose();
  }

  return (
    <div className="Dashboard">
      <Layout />
      <Container disableGutters={true} sx={{ mt: 3 }}>
        <Hidden mdUp>
          <Box mb={4}>
            <Button to="/" variant="outlined" component={RouterLink}>
              <Home />
            </Button>
          </Box>
        </Hidden>
        <Hidden mdDown>
          <Box mb={4}>
            <Button
              to="/"
              variant="outlined"
              startIcon={<Home />}
              component={RouterLink}
            >
              {t("Back to Home")}
            </Button>
          </Box>
        </Hidden>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h2">{t("app")}</Typography>
          <Hidden smDown>
            <Button
              variant="contained"
              onClick={() => setOpenUploadDialog(true)}
              startIcon={<Smartphone />}
            >
              {t("New Application")}
            </Button>
          </Hidden>
          <Hidden smUp>
            <Button
              variant="contained"
              onClick={() => setOpenUploadDialog(true)}
              startIcon={<Smartphone />}
            >
              {t("New Application")}
            </Button>
          </Hidden>
        </Stack>
        <UploadingAppForm
          buttonText={t("New Application")}
          dialogTitle={t("Upload a new application")}
          isDashboard={true}
          openDialog={openUploadDialog}
          updateOpenUploadDialog={setOpenUploadDialog}
        />
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <TextField
              sx={{
                width: "300px",
                borderRadius: 1.5,
                backgroundColor: "white",
              }}
              color="primary"
              placeholder={t("Search Application") + "..."}
              margin="dense"
              id="description"
              type="text"
              variant="outlined"
              onChange={(event) => handleSearch(event)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "primary" }} />
                  </InputAdornment>
                ),
              }}
            />
            {
              <Hidden mdDown>
                <IconButton
                  id="basic-button"
                  aria-controls={open ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleClick}
                >
                  <FilterListIcon />
                </IconButton>
                <Menu
                  id="basic-menu"
                  anchorEl={anchorEl}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    "aria-labelledby": "basic-button",
                  }}
                >
                  <MenuItem key="all" onClick={() => handleMenuItem(undefined)}>
                    {t("view_all")}
                  </MenuItem>
                  {groups.map((group, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => handleMenuItem(group.id)}
                    >
                      {group.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Hidden>
            }
            <Hidden mdUp>
              <Button
                id="basic-button"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
              >
                <Hidden smDown>{t("Sort by: ")}</Hidden>
                <Typography
                  component="span"
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  {cardOrder}
                </Typography>
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <MenuItem
                  key="asc-name"
                  onClick={() => sortCards("ascending-name")}
                >
                  {t("Name")}
                </MenuItem>
                <MenuItem
                  key="desc-time"
                  onClick={() => sortCards("descending-time")}
                >
                  {t("Latest")}
                </MenuItem>
                <MenuItem
                  key="asc-time"
                  onClick={() => sortCards("ascending-time")}
                >
                  {t("Oldest")}
                </MenuItem>
              </Menu>
            </Hidden>
          </Stack>
          <Paper>
            <Hidden mdDown>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell key="icon" align="center" width="16%">
                        {t("Icon")}
                      </TableCell>
                      <TableCell
                        key="name"
                        align="center"
                        width="16%"
                        sx={{ maxWidth: "25%" }}
                      >
                        <TableSortLabel
                          active={orderBy === "name"}
                          direction={
                            orderBy === "name" ? orderDirection : "asc"
                          }
                          onClick={() => handleRequestSort("name")}
                        >
                          {t("Name")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell key="platform" align="center" width="16%">
                        {t("Platform")}
                      </TableCell>
                      {/* <TableCell key="description" align="center" width="16%">{t("Description")}</TableCell> */}
                      <TableCell key="release-date" align="center" width="16%">
                        <TableSortLabel
                          active={orderBy === "updated_at"}
                          direction={
                            orderBy === "updated_at" ? orderDirection : "asc"
                          }
                          onClick={() => handleRequestSort("updated_at")}
                        >
                          {t("Updated at")}
                        </TableSortLabel>
                      </TableCell>
                      {localStorage.getItem("Role") !== "USER" && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingApps ? (
                      skeletonArray.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            width="20%"
                          >
                            <Skeleton />
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            width="20%"
                          >
                            <Skeleton />
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            width="20%"
                          >
                            <Skeleton />
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            width="20%"
                          >
                            <Skeleton />
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            width="20%"
                          >
                            <Skeleton />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : data4Table.length > 0 ? (
                      stableSort(
                        data4Table,
                        getComparator(orderDirection, orderBy)
                      )
                        .filter((app) => {
                          if (groupId !== undefined) {
                            return app.groupId === groupId;
                          } else {
                            return true;
                          }
                        })
                        .filter((element) => {
                          if (searchApp === "") {
                            return element;
                          } else if (
                            element.name
                              .toLowerCase()
                              .includes(searchApp.toLowerCase())
                          ) {
                            return element;
                          }
                        })
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((element: Application4Table) => (
                          <TableRow
                            key={element.id}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell width="16%%" align="center">
                              <Link
                                to={"/applications/" + element.name}
                                state={{
                                  id: element.id,
                                  appName: element.name,
                                }}
                                style={{ textDecoration: "none" }}
                              >
                                <Box display="flex" justifyContent="center">
                                  <Avatar
                                    alt={element.name}
                                    src={`${process.env.REACT_APP_BACKEND_BASE_URL}apps/${element.id}/icon`}
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      borderRadius: 1.5,
                                    }}
                                  />
                                </Box>
                              </Link>
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              width="16%"
                              align="center"
                            >
                              <Link
                                to={"/applications/" + element.name}
                                state={{
                                  id: element.id,
                                  appName: element.name,
                                }}
                                style={{ textDecoration: "none" }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ color: "text.primary" }}
                                  noWrap
                                >
                                  {element.name}
                                </Typography>
                              </Link>
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              width="16%"
                              align="center"
                            >
                              {(element.platform === "Android; iOS;" ||
                                element.platform === "iOS") && (
                                <Apple
                                  color="action"
                                  sx={{ width: 25, height: 25 }}
                                />
                              )}
                              {(element.platform === "Android; iOS;" ||
                                element.platform === "Android") && (
                                <Android
                                  color="action"
                                  sx={{ width: 25, height: 25 }}
                                />
                              )}
                            </TableCell>
                            <TableCell width="20%" align="center">
                              {formatDistance(
                                new Date(element.updated_at),
                                new Date(),
                                {
                                  locale:
                                    localStorage.getItem("lng") === "it"
                                      ? it
                                      : enUS,
                                }
                              )}
                            </TableCell>
                            {localStorage.getItem("Role") !== "USER" && (
                              <TableCell width="16%" align="center">
                                <Tooltip title={title}>
                                  <Button
                                    onClick={() => handleSetDeleteInfo(element)}
                                  >
                                    <Delete color="action" />
                                  </Button>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={
                            localStorage.getItem("Role") !== "USER" ? 6 : 5
                          }
                        >
                          {data4Table.length === 0 && !isErrorApps ? (
                            <Stack alignItems="center" spacing={1}>
                              <DriveFolderUpload
                                sx={{ fontSize: 40, color: grey[500] }}
                              />
                              <NoDataToBeShown />
                              <Typography>
                                {t("Upload a new application to start.")}
                              </Typography>
                            </Stack>
                          ) : (
                            isErrorApps && <ErrorComponent />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                labelRowsPerPage={t("Rows per page")}
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={apps.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Hidden>
          </Paper>
          <Hidden mdUp>
            <AppsCard
              open={openDelete}
              setOpen={setOpenDelete}
              reverseData={apps.filter((app) => {
                if (searchApp === "") {
                  return app;
                } else if (
                  app.name.toLowerCase().includes(searchApp.toLowerCase())
                ) {
                  return app;
                }
              })}
              isLoading={isLoadingApps}
              deletedApp={deletedApp}
              setDeletedApp={setDeletedApp}
            />
          </Hidden>
        </Stack>
        {deletedApp && (
          <DeleteElementFromTable
            open={openDelete}
            setOpen={setOpenDelete}
            entity="apps"
            id={deletedApp.id}
            name={deletedApp.name}
            appArray={apps}
          />
        )}
      </Container>
      {apps.length > 0 || (isErrorApps && apps.length === 0) ? (
        <div
          style={{
            position: "relative",
            left: 0,
            bottom: 0,
            right: 0,
            paddingTop: 40,
          }}
        >
          <Footer />
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            right: 0,
          }}
        >
          <Footer />
        </div>
      )}
    </div>
  );
}

reportWebVitals();
